import React, { useState } from 'react';
import { styled, Text } from 'hyarchis-global';
import { useActions, useShallowSelector } from 'hooks';
import { ApplicationState } from 'store/types';
import { updateSettings as updateSettingsAction } from 'store/settings/actions';
import { OutputMap, SettingsState } from 'store/settings/types';
import Collapsible from 'react-collapsible';
import Button from 'antd/lib/button';

const FormGroup = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.border};
  padding: 10px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const MappingsTitle = styled.span`
  font-weight: bold;
`;

const SettingsEditDrawer = () => {
  const { settings } = useShallowSelector((app: ApplicationState) => ({ settings: app.settings }));
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState<boolean>(false);
  const { outputMap } = settings;

  const { updateSettings } = useActions({
    updateSettings: updateSettingsAction,
  });

  const changeSettings = (partialSettings: Partial<SettingsState>) => {
    updateSettings({
      ...settings,
      ...partialSettings,
    });
  };

  const changeOutputMap = (partialOutputMap: Partial<OutputMap>) => {
    const updatedOutputMap = {
      ...outputMap,
      ...partialOutputMap,
    };
    updateSettings({
      ...settings,
      outputMap: updatedOutputMap,
    });
  };
  const toggleAdvancedSettigns = () => setAdvancedSettingsOpen(!advancedSettingsOpen);

  return (
    <div style={{ overflowX: 'hidden', overflowY: 'scroll', height: '100%' }}>
      <FormGroup>
        <Text.Validatable
          required
          label='WorkflowId'
          value={settings.workflowId}
          onChange={(v) => changeSettings({ workflowId: v?.toString() })}
        />
      </FormGroup>
      <FormGroup>
        <Text.Validatable
          required
          label='API endpoint'
          value={settings.endpoint}
          onChange={(v) => changeSettings({ endpoint: v?.toString() })}
        />
        <Text.Validatable
          required
          label='Client Id'
          value={settings.clientId}
          onChange={(v) => changeSettings({ clientId: v?.toString() })}
        />
        <Text.Validatable
          required
          label='Client Secret'
          value={settings.clientSecret}
          onChange={(v) => changeSettings({ clientSecret: v?.toString() })}
        />
      </FormGroup>
      <Collapsible trigger='' open={advancedSettingsOpen}>
        <FormGroup>
          <MappingsTitle>Output mappings</MappingsTitle>
          <Text.Validatable
            required
            label='Pdf file'
            value={outputMap.pdfFileId}
            onChange={(v) => changeOutputMap({ pdfFileId: v?.toString() })}
          />
          <Text.Validatable
            label='Text file'
            value={outputMap.textFileId}
            onChange={(v) => changeOutputMap({ textFileId: v?.toString() })}
          />
          <Text.Validatable
            required
            label='Document type'
            value={outputMap.documentType}
            onChange={(v) => changeOutputMap({ documentType: v?.toString() })}
          />
          <Text.Validatable
            required
            label='ID predictions'
            value={outputMap.idPredictions}
            onChange={(v) => changeOutputMap({ idPredictions: v?.toString() })}
          />
          <Text.Validatable
            required
            label='PII'
            value={outputMap.pii}
            onChange={(v) => changeOutputMap({ pii: v?.toString() })}
          />
          <Text.Validatable
            required
            label='Document data extraction'
            value={outputMap.documentDataExtraction}
            onChange={(v) => changeOutputMap({ documentDataExtraction: v?.toString() })}
          />
          <Text.Validatable
            required
            label='Pages data extraction'
            value={outputMap.pagesDataExtraction}
            onChange={(v) => changeOutputMap({ pagesDataExtraction: v?.toString() })}
          />
          <Text.Validatable
            required
            label='Data extraction V2'
            value={outputMap.dataExtractionV2}
            onChange={(v) => changeOutputMap({ dataExtractionV2: v?.toString() })}
          />
          <Text.Validatable
            required
            label='Id cut-out'
            value={outputMap.idCutOut}
            onChange={(v) => changeOutputMap({ idCutOut: v?.toString() })}
          />
          <Text.Validatable
            required
            label='ID verification'
            value={outputMap.faceVerification}
            onChange={(v) => changeOutputMap({ faceVerification: v?.toString() })}
          />
          <Text.Validatable
            label='Risk Assessment'
            value={outputMap.riskAssessment}
            onChange={(v) => changeOutputMap({ riskAssessment: v?.toString() })}
          />
          <Text.Validatable
            label='Risk Assessment Csv'
            value={outputMap.fileId}
            onChange={(v) => changeOutputMap({ fileId: v?.toString() })}
          />
          <Text.Validatable
            required
            label='Merge pdf file'
            value={outputMap.mergedFileId}
            onChange={(v) => changeOutputMap({ mergedFileId: v?.toString() })}
          />
          <Text.Validatable
            label='Cross match results'
            value={outputMap.crossMatchResults}
            onChange={(v) => changeOutputMap({ crossMatchResults: v?.toString() })}
          />
        </FormGroup>
      </Collapsible>
      <ButtonWrapper>
        <Button type='link' onClick={toggleAdvancedSettigns}>
          {advancedSettingsOpen ? 'Hide advanced settings' : 'Show advanced settings'}
        </Button>
      </ButtonWrapper>
    </div>
  );
};

export default React.memo(SettingsEditDrawer);
